/* ─── Theme → CSS variable injection ─── */

import type { ThemeConfig } from './types';

const CSS_VAR_MAP: Record<string, string> = {
  primary: '--artifact-primary',
  background: '--artifact-background',
  foreground: '--artifact-foreground',
  muted: '--artifact-muted',
  borderRadius: '--artifact-border-radius',
};

/* ─── Brand palette (dark-only) ───────────────────────────────────
   Ink (#222222)      default background
   Paper (#FFFDF9)    default text
   Periwinkle (#7678ED)  primary / accent — use sparingly
   Lavender (#D7DFF5)    subtle highlights, hover/selected backgrounds
   Dark Grey (#323232)   muted surfaces, borders
   Light Grey (#EDEDED)  kept available for Paper-on-Ink details
   The renderer is dark-only; the `darkMode` flag is accepted for
   backwards compatibility but does not change the palette.
   ───────────────────────────────────────────────────────────────── */
const DEFAULT_THEME: Record<string, string> = {
  '--artifact-primary': '#7678ED',
  '--artifact-background': '#222222',
  '--artifact-foreground': '#FFFDF9',
  '--artifact-muted': '#323232',
  '--artifact-border-radius': '8px',
  '--artifact-radius-sm': '6px',
  '--artifact-font': "'ABC Arizona Sans', 'Arizona Sans', system-ui, -apple-system, 'Helvetica Neue', sans-serif",
  '--artifact-font-headline': "'ABC Arizona Sans', 'Arizona Sans', system-ui, -apple-system, 'Helvetica Neue', sans-serif",
  '--artifact-font-mono': "'IBM Plex Mono', ui-monospace, 'Cascadia Code', 'JetBrains Mono', monospace",
  '--artifact-gap': '16px',

  '--artifact-surface': '#2A2A2A',
  '--artifact-border': '#323232',
  '--artifact-accent': '#7678ED',
  '--artifact-accent-hover': '#8B8DF0',
  '--artifact-accent-light': 'rgba(215, 223, 245, 0.12)',
  '--artifact-lavender': '#D7DFF5',
  '--artifact-text-secondary': '#B8B8B8',
  '--artifact-text-tertiary': '#7A7A7A',
  '--artifact-surface-header': '#1C1C1C',

  '--artifact-success': '#5FBF6E',
  '--artifact-danger': '#E85C5C',
  '--artifact-warning': '#F0B840',
  '--artifact-flat': '#F0B840',

  '--artifact-shadow': '0 1px 3px rgba(0,0,0,0.3)',
  '--artifact-shadow-lg': '0 8px 24px rgba(0,0,0,0.35)',
  '--artifact-panel-bg': 'rgba(255,253,249,0.03)',
  '--artifact-panel-border': 'rgba(255,253,249,0.08)',
  '--artifact-overlay': 'rgba(0,0,0,0.55)',
  '--artifact-on-primary': '#FFFDF9',
  '--artifact-code-bg': '#1C1C1C',
  '--artifact-code-fg': '#E8E8E8',
  '--artifact-code-muted': '#7A7A7A',
};

/* Light variant — matches the paper-light chat shell. Brand palette
   (paper / cream / bone / ink + periwinkle) with cool gray text scale. */
const LIGHT_THEME: Record<string, string> = {
  ...DEFAULT_THEME,
  '--artifact-primary': '#7678E3',
  '--artifact-background': '#F5F3E7',
  '--artifact-foreground': '#0A0A0A',
  '--artifact-muted': '#F0EDE0',

  '--artifact-surface': '#F5F3E7',
  '--artifact-border': 'rgba(10,10,10,0.10)',
  '--artifact-accent': '#7678E3',
  '--artifact-accent-hover': '#7678ED',
  '--artifact-accent-light': 'rgba(118,120,227,0.10)',
  '--artifact-lavender': '#D7DFF5',
  '--artifact-text-secondary': '#56575A',
  '--artifact-text-tertiary': '#75767E',
  '--artifact-surface-header': '#F0EDE0',

  '--artifact-shadow': '0 1px 3px rgba(0,0,0,0.06)',
  '--artifact-shadow-lg': '0 8px 24px rgba(0,0,0,0.10)',
  '--artifact-panel-bg': 'rgba(10,10,10,0.02)',
  '--artifact-panel-border': 'rgba(10,10,10,0.08)',
  '--artifact-overlay': 'rgba(10,10,10,0.30)',
  '--artifact-on-primary': '#FFFDF9',
  '--artifact-code-bg': '#F0EDE0',
  '--artifact-code-fg': '#1E1E1E',
  '--artifact-code-muted': '#75767E',
};

const isPaperLight = (): boolean => {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('theme-paper-light');
};

export type PanelMode = 'light' | 'dark';

const renderVarBlock = (vars: Record<string, string>): string =>
  Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');

/**
 * Render the artifact CSS-variable palette as a self-contained ``<style>``
 * block keyed off ``[data-panel-mode]``. Used by the HTML escape hatch to
 * give the iframe the same dark/light tokens that the panel container
 * exposes — so raw HTML using ``var(--artifact-foreground)`` etc. flips
 * automatically when the parent toggles theme. Default ``:root`` carries
 * the dark palette; ``[data-panel-mode="light"]`` overrides only the
 * mode-dependent slots.
 */
export const renderArtifactThemeStyles = (): string =>
  `:root {\n${renderVarBlock(DEFAULT_THEME)}\n}\n` +
  `:root[data-panel-mode="light"] {\n${renderVarBlock(LIGHT_THEME)}\n}\n`;

export function applyTheme(
  theme: ThemeConfig,
  container: HTMLElement,
  _clientDarkMode?: boolean,
): void {
  const palette = isPaperLight() ? LIGHT_THEME : DEFAULT_THEME;
  for (const [prop, value] of Object.entries(palette)) {
    container.style.setProperty(prop, value);
  }

  for (const [key, value] of Object.entries(theme)) {
    const cssVar = CSS_VAR_MAP[key];
    if (cssVar && typeof value === 'string') {
      container.style.setProperty(cssVar, value);
    }
  }
}
